import { ISerializable } from "./base";
import { Address, FormattedAddress, User } from "./user";
import moment from "moment";
import { ContainerFile, Project } from "./project";
import { Dictionary } from "../../../utils/src/lib/collections";
import { Price } from "./price";

export class Order implements ISerializable<Order> {
  id: string | undefined;
  name: string | undefined;
  orderNumber: string | undefined;
  createdAt: Date | undefined;
  cancelledAt: Date | undefined;
  updatedAt: Date | undefined;
  closedAt: Date | undefined;
  processedAt: Date | undefined;
  financialStatus: string | undefined;
  fulfillmentStatus: string | undefined;
  confirmed: boolean | undefined;
  note: string | undefined;
  allSamples: boolean | undefined;

  totalPrice: number | undefined;
  subtotalPrice: number | undefined;
  shippingPrice: number | undefined;
  totalWeight: number | undefined;
  totalTax: number | undefined;
  taxesIncluded: boolean | undefined;
  discounts: number | undefined;
  totalLineItemsPrice: number | undefined;

  shippingAddress: FormattedAddress | undefined;
  billingAddress: FormattedAddress | undefined;

  customer: Customer | undefined;
  contactEmail: string | undefined;
  user: User | undefined; // Not in initial order response

  draftId: string | undefined;
  draftName: string | undefined;

  transactions: OrderTransaction[] = [];

  lineItems: LineItem[] = [];
  shippingLines: ShippingLine[] = [];
  fulfillments: Fulfillment[] = [];

  hasLineItems: boolean | undefined = false;
  hasShippingLines: boolean | undefined = false;
  hasFulfillments: boolean | undefined = false;

  next: string | undefined;
  previous: string | undefined;

  properties: Dictionary<string, string> = new Dictionary<string, string>();
  metafields: Metafield[] = [];
  noteAttributes: NoteAttribute[] = [];
  status: Dictionary<string, string> = new Dictionary<string, string>();
  packaging: LineItemPackaging | undefined;

  events: OrderEvent[] = [];

  deserialize(input: any): Order {
    this.id = input.id;
    this.name = input.name;
    this.orderNumber = input.orderNumber;
    if (input.created_at) {
      this.createdAt = moment(input.created_at).toDate();
    }
    if (input.updated_at) {
      this.updatedAt = moment(input.updated_at).toDate();
    }
    if (input.closed_at) {
      this.closedAt = moment(input.closed_at).toDate();
    }
    if (input.cancelled_at) {
      this.cancelledAt = moment(input.cancelled_at).toDate();
    }
    if (input.processed_at) {
      this.processedAt = moment(input.processed_at).toDate();
    }
    if (input.note && input.note !== "null") {
      this.note = input.note;
    }
    this.allSamples = input.allSamples;
    // console.log('note', this.note);
    this.totalPrice = input.total_price;
    this.subtotalPrice = input.subtotal_price;
    this.totalWeight = input.total_weight;
    this.shippingPrice = input.total_shipping;
    this.totalTax = input.total_tax;
    this.taxesIncluded = input.taxes_included;
    this.financialStatus = input.financial_status;
    this.fulfillmentStatus = input.fulfillment_status;
    if (!this.fulfillmentStatus) {
      this.fulfillmentStatus = "unfulfilled";
    }
    this.confirmed = input.confirmed;
    this.discounts = input.discounts;
    this.totalLineItemsPrice = input.total_line_items_price;
    if (input.shipping_address_formatted) {
      this.shippingAddress = new FormattedAddress().deserialize(input.shipping_address_formatted);
    }
    if (input.billing_address_formatted) {
      this.billingAddress = new FormattedAddress().deserialize(input.billing_address_formatted);
    }
    if (input.transactions) {
      (input.transactions as any[]).forEach((transaction) => {
        const t = new OrderTransaction().deserialize(transaction);
        this.transactions.push(t);
      });
    }
    if (input.customer) {
      this.customer = new Customer().deserialize(input.customer);
      // console.log('customer', input.customer);
    }
    this.draftId = input.draft_id;
    this.draftName = input.draft_name;
    this.contactEmail = input.contactEmail;
    if (input.line_items) {
      this.hasLineItems = true;
      (input.line_items as any[]).forEach((lineItem) => {
        this.lineItems.push(new LineItem().deserialize(lineItem));
      });
    }
    if (input.shipping_lines) {
      this.hasShippingLines = true;
      (input.shipping_lines as any[]).forEach((shippingLine) => {
        const sl = new ShippingLine().deserialize(shippingLine);
        this.shippingLines.push(sl);
      });
    }
    if (input.fulfillments) {
      (input.fulfillments as any[]).forEach((fulfillment) => {
        const f: Fulfillment = new Fulfillment().deserialize(fulfillment);
        if (f.lineItems.length > 0) {
          this.hasFulfillments = true;
        }
        this.fulfillments.push(f);
      });
    }
    this.metafields = [];
    if (input.metafields) {
      (input.metafields as any[]).forEach((metafield) => {
        this.metafields.push(new Metafield().deserialize(metafield));
      });
    }
    this.noteAttributes = [];
    if (input.note_attributes) {
      (input.note_attributes as any[]).forEach((noteAttribute) => {
        this.noteAttributes.push(new NoteAttribute().deserialize(noteAttribute));
      });
    }
    this.next = input.next;
    this.previous = input.previous;
    if (input.properties) {
      (Object.getOwnPropertyNames(input.properties) as string[]).forEach((value) => {
        this.properties.add(value, input.properties[value]);
      });
    }
    if (input.status) {
      (Object.getOwnPropertyNames(input.status) as string[]).forEach((value) => {
        this.status.add(value, input.status[value]);
      });
    }
    if (!this.isDraft && this.hasLineItems) {
      this.packaging = new LineItemPackaging().deserialize(this);
    }

    this.events = [];
    (input.events as any[]).forEach((event) => {
      this.events.push(new OrderEvent().deserialize(event));
    });

    // console.log('', this.status);

    return this;
  }

  get isResellerOrder(): boolean {
    return this.getNoteAttribute("Quote ID") !== undefined;
  }

  get isReorder(): boolean {
    let reorder = false;
    this.lineItems.forEach((value) => {
      if (value.reorder && value.reorder.orderId) {
        reorder = true;
      }
    });
    return reorder;
  }

  get quoteId(): string | undefined {
    const noteAttribute = this.getNoteAttribute("Quote ID");
    if (noteAttribute) {
      return noteAttribute.value;
    }
    return undefined;
  }

  get isProofCreated(): boolean {
    return (
      this.lineItems
        .filter((value) => value.project)
        .filter((value) => value.project?.proofNeeded)
        .map((value) => value.project?.proofCreated)
        // .map(value => value !== undefined)
        .reduce(
          (previousValue, currentValue) => previousValue && currentValue !== undefined && currentValue !== null,
          true
        )
    );
  }

  get proofCreated(): Date {
    const d = this.lineItems
      .filter((value) => value.project)
      .filter((value) => value.project?.proofNeeded)
      .map((value) => value.project?.proofCreated)
      .filter((value) => value)
      .map((value) => moment(value))
      .reduce(
        (previousValue, currentValue) =>
          previousValue && previousValue.isAfter(currentValue) ? previousValue : currentValue,
        moment(0)
      );
    if (d) {
      return d.toDate();
    }
    return new Date(0);
  }

  get isProofApproved(): boolean {
    return (
      this.lineItems
        .filter((value) => value.project)
        .filter((value) => value.project?.proofNeeded)
        // .map(value => {
        //     console.log('v', value);
        //     return value;
        // })
        .map((value) => value.project?.proofApproved)
        // .map(value => value !== undefined)
        .reduce(
          (previousValue, currentValue) => previousValue && currentValue !== undefined && currentValue !== null,
          true
        )
    );
  }

  get proofApproved(): Date | undefined {
    const d = this.lineItems
      .filter((value) => value.project)
      .filter((value) => value.project?.proofNeeded)
      .map((value) => value.project?.proofApproved)
      .filter((value) => value)
      .map((value) => moment(value))
      .reduce(
        (previousValue, currentValue) =>
          previousValue && previousValue.isAfter(currentValue) ? previousValue : currentValue,
        moment(0)
      );
    if (d) {
      return d.toDate();
    }
    return undefined;
  }

  set proofApproved(date: Date | undefined) {
    this.setStatusDateValue("PROOF_APPROVED", date);
  }

  get isDelivered(): boolean {
    return this.status.containsKey("DELIVERED");
  }

  get delivered(): Date | undefined {
    return this.getStatusDateValue("DELIVERED");
  }

  set delivered(date: Date | undefined) {
    this.setStatusDateValue("DELIVERED", date);
  }

  get isReceived(): boolean {
    return this.status.containsKey("RECEIVED");
  }

  get received(): Date | undefined {
    return this.getStatusDateValue("RECEIVED");
  }

  get isRefunded(): boolean {
    return this.status.containsKey("REFUNDED");
  }

  get refunded(): Date | undefined {
    return this.getStatusDateValue("REFUNDED");
  }

  get isPaymentCaptured(): boolean {
    return this.status.containsKey("PAYMENT_CAPTURED");
  }

  get paymentCaptured(): Date | undefined {
    return this.getStatusDateValue("PAYMENT_CAPTURED");
  }

  get isFullyPaid(): boolean {
    return this.status.containsKey("FULL_PAYMENT");
  }

  get fullyPaid(): Date | undefined {
    return this.getStatusDateValue("FULL_PAYMENT");
  }

  get isShipped(): boolean {
    return this.status.containsKey("SHIPPED");
  }

  get shipped(): Date | undefined {
    return this.getStatusDateValue("SHIPPED");
  }

  get isCompleted(): boolean {
    return this.status.containsKey("COMPLETED");
  }

  get completed(): Date | undefined {
    return this.getStatusDateValue("COMPLETED");
  }

  get delivery(): Date | undefined {
    const noteAttribute = this.getNoteAttribute("DELIVERY");
    if (noteAttribute && noteAttribute.value) {
      return moment(noteAttribute.value, "MM/DD/YYYY hh:mm a").toDate();
    }
    return undefined;
  }

  set delivery(date: Date | undefined) {
    if (!date || date === null) {
      this.removeNoteAttribute("DELIVERY");
      return;
    }
    const formattedValue = moment(date).format("MM/DD/YYYY hh:mm a");
    this.putNoteAttribute("DELIVERY", formattedValue);
  }

  get showProof(): boolean {
    return this.properties.getValue("requiresProofing") as unknown as boolean;
  }

  get showShipped(): boolean {
    return this.properties.getValue("requiresShipping") as unknown as boolean;
  }

  get isDraft() {
    return this.properties.getValue("draft");
  }

  get isPartialPayment(): boolean {
    return this.financialStatus!.toLocaleLowerCase() === "partially_paid";
  }

  showEstimatedShipping() {
    return (
      this.refunded === undefined && this.showShipped && this.shipped === undefined && this.completed === undefined
    );
  }

  get orderStatus() {
    return this.closedAt ? "archived" : "open";
  }

  set orderStatus(status: string) {
    // ignored
  }

  get totalItems() {
    let total = 0;
    for (const item of this.lineItems) {
      total += item.quantity ?? 0;
    }
    return total;
  }

  private getStatusDateValue(key: string): Date | undefined {
    const value = this.status.getValue(key);
    if (value && value !== null && value !== "") {
      return moment(value, "MM/DD/YYYY hh:mm a").toDate();
    }
    return undefined;
  }

  private setStatusDateValue(key: any, date: Date | undefined) {
    if (!date || date === null) {
      this.removeNoteAttribute(key);
      return;
    }
    const formattedValue = moment(date).format("MM/DD/YYYY hh:mm a");
    if (this.status.containsKey(key)) {
      this.status.update(key, formattedValue);
    } else {
      this.status.add(key, formattedValue);
    }
    this.putNoteAttribute(key, formattedValue);
  }

  getNoteAttribute(name: string): NoteAttribute | undefined {
    for (const noteAttribute of this.noteAttributes) {
      if (noteAttribute.name === name) {
        return noteAttribute;
      }
    }
    return undefined;
  }

  putNoteAttribute(name: string, value: string) {
    let found = false;
    this.noteAttributes.forEach((noteAttribute) => {
      if (noteAttribute.name === name) {
        noteAttribute.value = value;
        found = true;
      }
    });
    if (!found) {
      this.noteAttributes.push(new NoteAttribute().deserialize({ name, value }));
    }
  }

  removeNoteAttribute(name: string) {
    this.properties.remove(name);
    this.noteAttributes.forEach((noteAttribute, index) => {
      if (noteAttribute.name === name) {
        this.noteAttributes.splice(index, 1);
        return;
      }
    });
  }
}

export class DraftOrder implements ISerializable<DraftOrder> {
  id: string | undefined;
  name: string | undefined;
  status: string | undefined;
  order: Order | undefined;
  customer: Customer | undefined;
  subtotalPrice: number | undefined;
  tax: number | undefined;
  totalPrice: number | undefined;
  createdAt: Date | undefined;
  completedAt: Date | undefined;
  invoiceSentAt: Date | undefined;
  invoiceUrl: string | undefined;
  lineItems: LineItem[] = [];
  noteAttributes: NoteAttribute[] = [];
  note: string | undefined;

  user: User | undefined;
  packaging: Packaging | undefined;

  deserialize(input: any): DraftOrder {
    this.id = input.id;
    this.name = input.name;
    if (input.order) {
      this.order = new Order().deserialize(input.order);
    }
    if (input.customer) {
      this.customer = new Customer().deserialize(input.customer);
    }
    this.subtotalPrice = input.subtotalPrice;
    this.totalPrice = input.totalPrice;
    this.tax = input.tax;
    if (input.createdAt) {
      this.createdAt = moment(input.createdAt).toDate();
    }
    if (input.completedAt) {
      this.completedAt = moment(input.completedAt).toDate();
    }
    if (input.invoiceSentAt) {
      this.invoiceSentAt = moment(input.invoiceSentAt).toDate();
    }
    this.lineItems = [];
    if (input.line_items) {
      input.line_items.forEach((value: any) => {
        this.lineItems.push(new LineItem().deserialize(value));
      });
    }
    this.noteAttributes = [];
    if (input.note_attributes) {
      (input.note_attributes as any[]).forEach((noteAttribute: any) => {
        this.noteAttributes.push(new NoteAttribute().deserialize(noteAttribute));
      });
    }
    if (this.hasLineItems) {
      this.packaging = new LineItemPackaging().deserialize(this);
    }
    this.invoiceUrl = input.invoiceUrl;
    this.note = input.note;
    return this;
  }

  get isResellerOrder(): boolean {
    return this.getNoteAttribute("Quote ID") !== undefined;
  }

  get quoteId(): string | undefined {
    const noteAttribute = this.getNoteAttribute("Quote ID");
    if (noteAttribute) {
      return noteAttribute.value;
    }
    return undefined;
  }

  get isCompleted(): boolean {
    return this.status === "COMPLETED";
  }

  get isOpen(): boolean {
    return this.status === "OPEN";
  }

  get hasLineItems(): boolean {
    return this.lineItems && this.lineItems.length > 0;
  }

  private getNoteAttribute(name: string): NoteAttribute | undefined {
    if (!this.noteAttributes) {
      return undefined;
    }
    for (const noteAttribute of this.noteAttributes) {
      if (noteAttribute.name === name) {
        return noteAttribute;
      }
    }
    return undefined;
  }
}
export class DraftOrderPage implements ISerializable<DraftOrderPage> {
  prevCursor: string | undefined;
  nextCursor: string | undefined;
  draftOrders: DraftOrder[] = [];

  deserialize(input: any): DraftOrderPage {
    this.prevCursor = input.prevCursor;
    this.nextCursor = input.nextCursor;
    this.draftOrders = [];
    (input.draftOrders as any[]).forEach((object: any) => {
      this.draftOrders.push(new DraftOrder().deserialize(object));
    });
    return this;
  }
}
export class OrderEvent implements ISerializable<OrderEvent> {
  id: string | undefined;
  createdAt: Date | undefined;
  attributeToUser: boolean | undefined;
  attributeToApp: boolean | undefined;
  criticalAlert: boolean | undefined;
  message: string | undefined;

  deserialize(input: any): OrderEvent {
    this.id = input.id;
    this.createdAt = moment(input.createdAt).toDate();
    this.attributeToApp = input.attributeToApp;
    this.attributeToUser = input.attributeToUser;
    this.criticalAlert = input.criticalAlert;
    this.message = input.message;
    return this;
  }
}
export class OrderTransaction implements ISerializable<OrderTransaction> {
  id: string | undefined;
  formattedGateway: string | undefined;
  gateway: string | undefined;
  accountNumber: string | undefined;
  createdAt: Date | undefined;
  kind: string | undefined;
  authorizationCode: string | undefined;
  amount: string | undefined;
  creditCardIconName: string | undefined; // not in payload
  paymentDescription: string | undefined; // not in payload

  deserialize(input: any): OrderTransaction {
    this.id = input.id;
    this.formattedGateway = input.formattedGateway;
    this.gateway = input.gateway;
    this.accountNumber = input.accountNumber;
    this.createdAt = moment(input.createdAt).toDate();
    this.kind = input.kind;
    this.authorizationCode = input.authorizationCode;
    this.amount = input.amount;

    switch (this.gateway) {
      /*
                        case 'American Express':
                            this.creditCardIconName = "fab fa-cc-amex";
                            break;
                        case 'Diners Club':
                            this.creditCardIconName = "fab fa-cc-diners-club";
                            break;
                        case 'Apple Pay':
                            this.creditCardIconName = "fab fa-cc-apple-pay";
                            break;
                        case 'Amazon Pay':
                            this.creditCardIconName = "fab fa-cc-amazon-pay";
                            break;
            */
      case "paypal":
        this.creditCardIconName = "fab fa-cc-paypal";
        this.paymentDescription = "PayPal Express";
        break;
      /*
                        case 'Discover':
                            this.creditCardIconName = "fab fa-cc-discover";
                            break;
            */
      case "shopify_payments":
        this.creditCardIconName = "fas fa-credit-card-blank";
        this.paymentDescription = "";
        break;
      case "manual":
        this.creditCardIconName = "money-check-alt";
        this.paymentDescription = "Manual";
        break;
    }
    return this;
  }
}
export class OrderCount implements ISerializable<OrderCount> {
  open: number | undefined;
  needsCapture: number | undefined;
  needsUrgentCapture: number | undefined;
  needsFulfillment: number | undefined;
  needsProof: number | undefined;
  needsProofApproval: number | undefined;

  deserialize(input: any): OrderCount {
    this.open = input.open;
    this.needsCapture = input.needsCapture;
    this.needsUrgentCapture = input.needsUrgentCapture;
    this.needsFulfillment = input.needsFulfillment;
    this.needsProof = input.needsProof;
    this.needsProofApproval = input.needsProofApproval;
    return this;
  }
}

export class LineItem implements ISerializable<LineItem> {
  id: string | undefined;
  name: string | undefined;
  title: string | undefined;
  sku: string | undefined;
  variantId: string | undefined;
  variantTitle: string | undefined;
  productId: string | undefined;
  reorder: Reorder | undefined;
  taxable: boolean | undefined;
  giftCard: boolean | undefined;
  price: number | undefined; // not part of original payload for order but is for draft order
  originalPrice: number | undefined;
  quantity: number | undefined;
  fulfillableQuantity: number | undefined;
  totalDiscount: number | undefined;
  requiresShipping: boolean | undefined;
  requiresProofing: boolean | undefined;

  originalUnitPrice: number | undefined;
  discountedUnitPrice: number | undefined;

  properties: Dictionary<string, string> = new Dictionary();

  containerName: string | undefined; // Not set when first read
  project: Project | undefined; // Not set when first read
  attributes: any = {};
  unfulfilledQuantity: number = 0;
  screenshotGraphics: ContainerFile | undefined;
  outsideGraphics: ContainerFile | undefined;
  insideGraphics: ContainerFile | undefined;
  proofGraphics: ContainerFile | undefined;
  rushOrder: boolean = false;
  paperType: string = "";
  isOtherProduct: boolean = false;
  prices: Price[] = [];
  selectedPrice: any;
  customQuantityActive: boolean = false;
  isTakeout: boolean = false;
  isTape: boolean = false;
  isCrinkle: boolean = false;
  isPizza: boolean = false;
  isPaddedMailer: any;
  total: any;
  enteredCustomQuantity: number | undefined;
  isSample: boolean = false;
  customQuantityViolation = false;
  titleAsHtml = '';
  variant: any | undefined;

  get hasDiscount(): boolean {
    return this.discountedUnitPrice !== this.originalUnitPrice;
  }

  get paper(): string {
    let boardType = "";
    switch (this.properties.getValue("board_type")) {
      case "k":
        boardType = "Kraft";
        break;
      case "kw":
        boardType = "Mottled White";
        break;
      case "pkw":
        boardType = "Kemi";
        break;
    }

    let boardStrength = this.properties.getValue("board_strength");
    if (!boardStrength) {
      boardStrength = "";
    } else {
      boardStrength += " ECT";
    }
    return boardType + " " + boardStrength;
  }

  get printer(): string {
    return this.properties.getValue("luxe") === "true" ? "C-500" : "C-17000";
  }

  get opv(): string {
    return this.properties.getValue("gloss") === "true" ? "Gloss" : "Matte";
  }

  deserialize(input: any): LineItem {
    this.id = input.id;
    this.name = input.name;
    this.title = input.title;
    this.sku = input.sku;
    this.variantId = input.variant_id;
    this.variantTitle = input.variant_title;
    this.productId = input.product_id;
    this.taxable = input.taxable;
    this.giftCard = input.gift_card;
    this.quantity = input.quantity;
    this.fulfillableQuantity = input.fulfillable_quantity;
    this.unfulfilledQuantity = input.unfulfilled_quantity;
    this.totalDiscount = input.total_discount;
    this.discountedUnitPrice = input.discounted_unit_price;
    this.originalUnitPrice = input.original_unit_price;
    this.requiresShipping = input.requires_shipping;
    this.requiresProofing = input.requires_proofing;
    this.attributes = input.attributes;

    if (this.discountedUnitPrice) {
      if (this.fulfillableQuantity) {
        this.price = this.fulfillableQuantity * this.discountedUnitPrice;
        this.originalPrice = this.fulfillableQuantity * this.originalUnitPrice!;
      } else {
        this.price = (this.quantity ?? 0) * this.discountedUnitPrice;
        this.originalPrice = (this.quantity ?? 0) * this.originalUnitPrice!;
      }
    } else {
      if (this.fulfillableQuantity) {
        this.originalPrice = this.fulfillableQuantity * this.originalUnitPrice!;
      } else {
        this.originalPrice = (this.quantity ?? 0) * this.originalUnitPrice!;
      }
    }
    if (input.properties) {
      (input.properties as any[]).forEach((property) => {
        this.properties.add(property.name, property.value);
      });
    }
    if (input.reorder) {
      this.reorder = new Reorder().deserialize(input.reorder);
    }

    return this;
  }
}

export class Reorder implements ISerializable<Reorder> {
  orderName: string | undefined;
  lineItemOrderName: string | undefined;
  orderId: string | undefined;

  deserialize(input: any): Reorder {
    this.orderName = input.orderName;
    this.lineItemOrderName = input.lineItemOrderName;
    this.orderId = input.orderId;
    return this;
  }
}

export class PaymentDetails implements ISerializable<PaymentDetails> {
  creditCardNumber: string | undefined;
  creditCardCompany: string | undefined;
  creditCardIconName: string | undefined;

  deserialize(input: any): PaymentDetails {
    this.creditCardCompany = input.payment_details.credit_card_company;
    this.creditCardNumber = input.payment_details.credit_card_number;

    if (this.creditCardCompany && input.paymentGatewayNames) {
      switch (input.paymentGatewayNames[0]) {
        case "American Express":
          this.creditCardIconName = "fab fa-cc-amex";
          break;
        case "Diners Club":
          this.creditCardIconName = "fab fa-cc-diners-club";
          break;
        case "Apple Pay":
          this.creditCardIconName = "fab fa-cc-apple-pay";
          break;
        case "Amazon Pay":
          this.creditCardIconName = "fab fa-cc-amazon-pay";
          break;
        default:
          this.creditCardIconName = "fab fa-cc-" + this.creditCardCompany.toLocaleLowerCase();
          break;
      }
    } else {
      this.creditCardIconName = "fas fa-credit-card-front";
    }

    return this;
  }
}

export class ShippingLine implements ISerializable<ShippingLine> {
  title: string | undefined;
  price: number | undefined;

  deserialize(input: any): ShippingLine {
    this.title = input.title;
    this.price = input.price;
    return this;
  }
}

export class Customer implements ISerializable<Customer> {
  id: string | undefined;
  displayName: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  address: Address | undefined;
  note: string | undefined;
  tags: string | undefined;

  get net30(): boolean {
    return this.hasTag("NET30");
  }

  get reseller(): boolean {
    return this.hasTag("RESELLER");
  }

  get vip(): boolean {
    return this.hasTag("VIP");
  }

  deserialize(input: any): Customer {
    this.id = input.id;
    this.displayName = input.displayName;
    this.phone = input.phone;
    this.note = input.note;
    this.email = input.email;
    this.tags = input.tags;
    if (input.default_address) {
      this.address = new Address().deserialize(input.default_address);
    }
    return this;
  }

  private hasTag(name: string) {
    let value = false;
    if (this.tags) {
      value = this.tags.indexOf(name) !== -1;
    }
    return value;
  }
}

export class Fulfillment implements ISerializable<Fulfillment> {
  createdAt: Date | undefined;
  updatedAt: Date | undefined;
  deliveredAt: Date | undefined;
  estimatedDeliveryAt: Date | undefined;

  status: string | undefined;
  trackingCompany: string | undefined;
  trackingNumber: string | undefined;
  trackingUrl: string | undefined;

  lineItems: LineItem[] = [];

  deserialize(input: any): Fulfillment {
    if (input.created_at) {
      this.createdAt = moment(input.created_at).toDate();
    }
    if (input.updated_at) {
      this.updatedAt = moment(input.updated_at).toDate();
    }
    if (input.delivered_at) {
      this.deliveredAt = moment(input.delivered_at).toDate();
    }
    if (input.estimated_delivery_at) {
      this.estimatedDeliveryAt = moment(input.estimated_delivery_at).toDate();
    }
    this.status = input.status;
    this.trackingCompany = input.tracking_company;
    this.trackingNumber = input.tracking_number;
    this.trackingUrl = input.tracking_url;
    if (input.line_items) {
      (input.line_items as any[]).forEach((lineItem) => {
        this.lineItems.push(new LineItem().deserialize(lineItem));
      });
    }
    return this;
  }
}

export class Metafield implements ISerializable<Metafield> {
  key: string | undefined;
  value: string | undefined;
  valueType: string | undefined;
  namespace: string | undefined;

  deserialize(input: any): Metafield {
    this.key = input.key;
    this.value = input.value;
    this.valueType = input.value_type;
    this.namespace = input.namespace;
    return this;
  }
}

export class NoteAttribute implements ISerializable<NoteAttribute> {
  name: string | undefined;
  value: string | undefined;

  deserialize(input: any): NoteAttribute {
    this.name = input.name;
    this.value = input.value;
    return this;
  }
}

export class LineItemPackaging implements ISerializable<LineItemPackaging> {
  type: string | undefined;
  quantity: number | undefined;
  shape: PackageShape | undefined;
  totalWeight: number | undefined;
  weight: number | undefined;
  freightClass: string | undefined;

  deserialize(input: any): LineItemPackaging {
    if (!input || !input.lineItems || input.lineItems.length === 0 || !input.lineItems[0].properties) {
      return new LineItemPackaging();
    }
    this.type = input.lineItems[0].properties.getValue("package_type");
    const dimensions = input.lineItems[0].properties.getValue("package_dimensions");
    if (dimensions) {
      const splitDimensions: string[] = dimensions?.split("x");
      this.shape = new PackageShape().deserialize({
        length: splitDimensions[0],
        width: splitDimensions[1],
        depth: splitDimensions[2],
      });
    }
    this.quantity = input.lineItems[0].properties.getValue("package_count");
    this.totalWeight = input.lineItems[0].properties.getValue("total_weight");
    this.weight = input.lineItems[0].properties.getValue("package_weight");
    this.freightClass = input.lineItems[0].properties.getValue("package_class");
    return this;
  }
}

export class PackageShape implements ISerializable<PackageShape> {
  length: number | undefined;
  width: number | undefined;
  depth: number | undefined;

  deserialize(input: any): PackageShape {
    this.length = input.length;
    this.width = input.width;
    this.depth = input.depth;
    return this;
  }
}

export class Packaging implements ISerializable<Packaging> {
  type: string | undefined;
  quantity: number | undefined;
  shape: PackageShape | undefined;
  totalWeight: number | undefined;
  weight: number | undefined;
  freightClass: string | undefined;

  deserialize(input: any): Packaging {
    this.type = input.type;
    this.quantity = input.quantity;
    this.shape = new PackageShape().deserialize(input.shape);
    this.totalWeight = input.totalWeight;
    this.weight = input.weight;
    this.freightClass = input.freightClass;
    return this;
  }
}
